import {
  type Canvas,
  Circle,
  type FabricObject,
  Line,
  Point,
  Rect,
} from "fabric";
import {
  DRAW_VERTEX_RADIUS,
  isNearFirstPoint,
  removePreviewLine,
  createPreviewLine,
  updatePreviewLine,
  updateFirstMarkerOnHover,
  snapToAxis,
} from "~/shared/lib/fabric-zone-drawing";
import type { FloorPlanDrawingTarget } from "./constants";
import { LINE_STROKE, ZONE_STROKE } from "./constants";
import {
  clampToImageBounds,
  getBgImage,
  getImageNaturalSize,
  getNormalizedPoint,
  pixelToPercentStatic,
} from "./canvas-helpers";

export interface DrawingState {
  tempObjects: FabricObject[];
  points: { x: number; y: number }[];
  rectDragStart?: { x: number; y: number } | null; // F19: rectangle drag start point
}

export interface DrawingContext {
  drawingStateRef: React.MutableRefObject<DrawingState | null>;
  drawingTargetRef: React.MutableRefObject<FloorPlanDrawingTarget | null | undefined>;
  onCompleteRef: React.MutableRefObject<((type: "line" | "zone", id: number, coordinates: { x: number; y: number }[]) => void) | undefined>;
  // 4-3-2: drawingRequest (id 없는 신규 그리기) 도 지원
  drawingRequestRef?: React.MutableRefObject<{ type: "line" | "zone"; eventType: string } | null | undefined>;
  onRequestCompleteRef?: React.MutableRefObject<((type: "line" | "zone", eventType: string, coordinates: { x: number; y: number }[]) => void) | undefined>;
}

// 현재 활성 그리기 모드 — target 우선, 없으면 request
function getActiveDrawing(ctx: DrawingContext) {
  const target = ctx.drawingTargetRef.current;
  if (target) return { mode: "target" as const, type: target.type, id: target.id };
  const request = ctx.drawingRequestRef?.current;
  if (request) return { mode: "request" as const, type: request.type, eventType: request.eventType };
  return null;
}

function fireComplete(ctx: DrawingContext, type: "line" | "zone", coords: { x: number; y: number }[]) {
  const target = ctx.drawingTargetRef.current;
  if (target) {
    ctx.onCompleteRef.current?.(type, target.id, coords);
    return;
  }
  const request = ctx.drawingRequestRef?.current;
  if (request) {
    ctx.onRequestCompleteRef?.current?.(type, request.eventType, coords);
  }
}

export function handleDrawingClick(canvas: Canvas, worldPt: Point, ctx: DrawingContext, shiftHeld?: boolean) {
  const active = getActiveDrawing(ctx);
  if (!active) return;

  const bg = getBgImage(canvas);
  const ns = getImageNaturalSize(canvas);
  let pt = { x: worldPt.x, y: worldPt.y };
  if (bg && ns) pt = clampToImageBounds(pt, bg, ns);

  if (!ctx.drawingStateRef.current) {
    ctx.drawingStateRef.current = { tempObjects: [], points: [] };
  }
  const state = ctx.drawingStateRef.current;

  // F19: Shift+line snap — on second click, snap to horizontal/vertical axis relative to first point
  if (shiftHeld && active.type === "line" && state.points.length === 1) {
    pt = snapToAxis(state.points[0], pt);
  }

  state.points.push(pt);

  const isLine = active.type === "line";
  const color = isLine ? LINE_STROKE : ZONE_STROKE;

  const marker = new Circle({
    left: pt.x,
    top: pt.y,
    radius: DRAW_VERTEX_RADIUS,
    fill: "#ffffff",
    stroke: color,
    strokeWidth: 1,
    originX: "center",
    originY: "center",
    selectable: false,
    evented: false,
  });
  canvas.add(marker);
  state.tempObjects.push(marker);

  if (isLine) {
    if (state.points.length === 2) {
      const coords = state.points.map((p2) => getNormalizedPoint(pixelToPercentStatic(p2, canvas)));
      clearDrawingTemp(canvas, ctx);
      fireComplete(ctx, "line", coords);
    } else if (state.points.length === 1) {
      const preview = new Line([pt.x, pt.y, pt.x, pt.y], {
        stroke: color,
        strokeWidth: 2,
        selectable: false,
        evented: false,
        strokeDashArray: [5, 5],
      });
      canvas.add(preview);
      state.tempObjects.push(preview);
    }
  } else {
    removePreviewLine(canvas, state);

    if (state.points.length >= 4 && isNearFirstPoint(state.points[state.points.length - 1], state, canvas.getZoom())) {
      state.points.pop();
      const coords = state.points.map((p2) => getNormalizedPoint(pixelToPercentStatic(p2, canvas)));
      clearDrawingTemp(canvas, ctx);
      fireComplete(ctx, "zone", coords);
      return;
    }

    if (state.points.length >= 2) {
      const prev = state.points[state.points.length - 2];
      const segLine = new Line([prev.x, prev.y, pt.x, pt.y], {
        stroke: color,
        strokeWidth: 2,
        selectable: false,
        evented: false,
        strokeDashArray: [5, 5],
      });
      canvas.add(segLine);
      state.tempObjects.push(segLine);
    }

    createPreviewLine(canvas, state, color);
  }

  canvas.requestRenderAll();
}

export function handleDrawingMove(canvas: Canvas, worldPt: Point, ctx: DrawingContext, shiftHeld?: boolean) {
  const state = ctx.drawingStateRef.current;
  const active = getActiveDrawing(ctx);
  if (!state || state.points.length === 0 || !active) return;

  const bg = getBgImage(canvas);
  const ns = getImageNaturalSize(canvas);
  let pt = { x: worldPt.x, y: worldPt.y };
  if (bg && ns) pt = clampToImageBounds(pt, bg, ns);

  // F19: Shift+line snap — preview snaps to horizontal/vertical axis
  if (shiftHeld && active.type === "line" && state.points.length === 1) {
    pt = snapToAxis(state.points[0], pt);
  }

  if (active.type === "line" && state.points.length === 1) {
    const previewLine = state.tempObjects.find((o) => o instanceof Line) as Line | undefined;
    if (previewLine) {
      previewLine.set({ x2: pt.x, y2: pt.y });
      canvas.requestRenderAll();
    }
  }

  if (active.type === "zone") {
    updatePreviewLine(canvas, state, pt);
    updateFirstMarkerOnHover(canvas, state, pt, canvas.getZoom());
  }
}

export function handleDrawingDblClick(canvas: Canvas, ctx: DrawingContext) {
  const state = ctx.drawingStateRef.current;
  const active = getActiveDrawing(ctx);
  if (!state || !active || active.type !== "zone") return;

  if (state.points.length > 1) state.points.pop();

  if (state.points.length >= 3) {
    const coords = state.points.map((p) => getNormalizedPoint(pixelToPercentStatic(p, canvas)));
    clearDrawingTemp(canvas, ctx);
    fireComplete(ctx, "zone", coords);
  } else {
    clearDrawingTemp(canvas, ctx);
  }
}

export function clearDrawingTemp(canvas: Canvas, ctx: DrawingContext) {
  const state = ctx.drawingStateRef.current;
  if (state) {
    for (const obj of state.tempObjects) canvas.remove(obj);
  }
  ctx.drawingStateRef.current = null;
  canvas.requestRenderAll();
}

// --- F19: Rectangle zone drawing handlers ---

const RECT_MIN_SIZE = 4;

export function handleRectDrawingMouseDown(canvas: Canvas, worldPt: Point, ctx: DrawingContext) {
  const active = getActiveDrawing(ctx);
  if (!active || active.type !== "zone") return;

  const bg = getBgImage(canvas);
  const ns = getImageNaturalSize(canvas);
  let pt = { x: worldPt.x, y: worldPt.y };
  if (bg && ns) pt = clampToImageBounds(pt, bg, ns);

  if (!ctx.drawingStateRef.current) {
    ctx.drawingStateRef.current = { tempObjects: [], points: [] };
  }
  ctx.drawingStateRef.current.rectDragStart = pt;
}

export function handleRectDrawingMouseMove(canvas: Canvas, worldPt: Point, ctx: DrawingContext) {
  const state = ctx.drawingStateRef.current;
  if (!state || !state.rectDragStart) return;

  const bg = getBgImage(canvas);
  const ns = getImageNaturalSize(canvas);
  let pt = { x: worldPt.x, y: worldPt.y };
  if (bg && ns) pt = clampToImageBounds(pt, bg, ns);

  const start = state.rectDragStart;
  const left = Math.min(start.x, pt.x);
  const top = Math.min(start.y, pt.y);
  const width = Math.abs(pt.x - start.x);
  const height = Math.abs(pt.y - start.y);

  // Remove existing rect preview
  const oldPreview = state.tempObjects.find((o) => (o as any)._isRectPreview);
  if (oldPreview) {
    canvas.remove(oldPreview);
    state.tempObjects = state.tempObjects.filter((o) => o !== oldPreview);
  }

  const rect = new Rect({
    left,
    top,
    width,
    height,
    originX: "left",
    originY: "top",
    fill: "rgba(25, 227, 255, 0.12)",
    stroke: ZONE_STROKE,
    strokeWidth: 2,
    strokeDashArray: [5, 5],
    selectable: false,
    evented: false,
  });
  (rect as any)._isRectPreview = true;
  canvas.add(rect);
  state.tempObjects.push(rect);
  canvas.requestRenderAll();
}

export function handleRectDrawingMouseUp(canvas: Canvas, worldPt: Point, ctx: DrawingContext) {
  const state = ctx.drawingStateRef.current;
  if (!state || !state.rectDragStart) return;

  const bg = getBgImage(canvas);
  const ns = getImageNaturalSize(canvas);
  let pt = { x: worldPt.x, y: worldPt.y };
  if (bg && ns) pt = clampToImageBounds(pt, bg, ns);

  const start = state.rectDragStart;
  const left = Math.min(start.x, pt.x);
  const top = Math.min(start.y, pt.y);
  const right = Math.max(start.x, pt.x);
  const bottom = Math.max(start.y, pt.y);

  state.rectDragStart = null;

  // Ignore too-small rectangles (accidental clicks)
  if (right - left < RECT_MIN_SIZE || bottom - top < RECT_MIN_SIZE) {
    clearDrawingTemp(canvas, ctx);
    return;
  }

  const coords = [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ].map((p) => getNormalizedPoint(pixelToPercentStatic(p, canvas)));

  clearDrawingTemp(canvas, ctx);
  fireComplete(ctx, "zone", coords);
}
